import { LitElement, html } from 'lit';
import { t } from '@lingui/macro';
import '../iconSetup';
import {
  contrastTextColor,
  fetchOptions,
  fetchPostsArray,
  getMediaType,
  initialFetch,
} from '../utils';
import { setHashParam, removeHashParam, setupHashListener } from '../hashUtils';
import '@appnest/masonry-layout';
import './components/MasonryTile/MasonryTile';
import './components/PostModal';
import {
  AnalyticsEvents,
  AnalyticsMaxPollTime,
  AnalyticsPollTime,
  AnalyticsScores,
  EmbedTypes,
} from '../consts';
// eslint-disable-next-line
import styles from '!!lit-css-loader!postcss-loader!sass-loader!./Masonry.scss';
// eslint-disable-next-line
import variables from '../styles/variables.module.scss';

const config = __gigg;

class MasonryWebComponent extends LitElement {
  static styles = [styles];

  static get properties() {
    return {
      posts: { type: Array },
      options: { type: Object },
      selectedPost: { type: Object },
      selectedPostIndex: { type: Number },
      totalPages: { type: Number },
      loading: { type: Boolean },
    };
  }

  constructor() {
    super();
    const preview = config.preview ? 'preview/' : '';
    this.apiURL = `${process.env.REACT_APP_CORE_API_URL}/fanboard/${preview}${config.accountId}/${config.folderId}`;
    this.options = {};
    this.posts = [];
    this.hashListenerCleanup = null;
    window.giggAnalyticsLoaded.then(async () => {
      window.giggAnalytics = new GiggAnalytics(process.env.REACT_APP_GIGG_ANALYTICS_KEY, {
        apiVersion: 1,
        env: process.env.REACT_APP_ENV,
        sessionKey: __gigg.folderId,
      });
      window.giggFirstEvent = await giggAnalytics.logEvent(AnalyticsEvents.EMBED_PAGE_VIEW, {
        boardId: __gigg.accountId,
        boardType: EmbedTypes.MASONRY,
        folderId: __gigg.folderId,
      });
      const interval = setInterval(() => {
        giggAnalytics.logEvent(AnalyticsEvents.EMBED_VIEW_POLL, {
          boardId: __gigg.accountId,
          boardType: EmbedTypes.MASONRY,
          folderId: __gigg.folderId,
          firstEvent: {
            _id: window.giggFirstEvent?._id,
            createdDate: window.giggFirstEvent?.createdDate,
          },
          inViewEvent: {
            _id: window.latestViewEvent?._id,
            createdDate: window.latestViewEvent?.createdDate,
          },
          isPoll: true,
          basePollFilter: 'firstEvent._id',
        });
      }, AnalyticsPollTime);
      setTimeout(() => clearInterval(interval), AnalyticsMaxPollTime);
    });
  }

  fetchPosts = async (mediaFilter, postLimit) => {
    const searchParams = new URLSearchParams();

    if (mediaFilter) {
      searchParams.append('postType', mediaFilter);
    }

    if (postLimit) {
      searchParams.append('size', postLimit);
    }

    this.searchParams = searchParams.toString();

    const initialResponse = await initialFetch(this.apiURL, this.searchParams);

    const { posts, ...responseRest } = initialResponse;

    if (postLimit < 30) {
      return {
        posts,
        ...responseRest,
      };
    }

    return initialResponse;
  };

  setStyleVariables = (options) => {
    this.style.setProperty('--gigg-primary-color', options.primaryColor || variables.primaryColor);
    this.style.setProperty(
      '--gigg-secondary-color',
      options.secondaryColor || variables.secondaryColor
    );
    this.style.setProperty(
      '--gigg-text-color',
      contrastTextColor(options.secondaryColor || variables.textColorPrimary)
    );
  };

  async connectedCallback() {
    super.connectedCallback();
    const preview = config.preview ? 'preview/' : '';
    const options = await fetchOptions(
      `${process.env.REACT_APP_CORE_API_URL}/fanboard/${preview}${config.accountId}/${config.folderId}/options`
    );

    this.setStyleVariables(options);
    const mediaFilter = options.mediaOnly && '!text';
    const results = await this.fetchPosts(mediaFilter, options.loadCountMasonry);
    if (config) {
      this.posts = results.posts;
      this.options = options;
      this.totalPages = results.totalPages;
      this.loadCountMasonry = options.loadCountMasonry;
      this.currentPage = 1;
      this.postsOpened = 0;
      this.postsFocused = 0;
      this.isHovered = false;

      this.hashListenerCleanup = setupHashListener(this.openPostByHashId, this.closeModal);
    }
  }

  /**
   * Cleanup when component is disconnected
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.hashListenerCleanup) {
      this.hashListenerCleanup();
    }
  }

  async handleSeeMore() {
    const searchParams = new URLSearchParams();
    const mediaFilter = this.options.mediaOnly && '!text';
    const postLimit = this.loadCountMasonry;

    if (mediaFilter) {
      searchParams.append('postType', mediaFilter);
    }

    if (postLimit) {
      searchParams.append('size', postLimit);
    }

    this.loading = true;
    const newPosts = await fetchPostsArray(
      this.apiURL,
      this.currentPage + 1,
      this.currentPage + 1,
      searchParams.toString()
    );

    this.loading = false;

    this.posts = [...this.posts, ...newPosts];
    this.currentPage += 1;
    giggAnalytics.logEvent(AnalyticsEvents.SEE_MORE, {
      boardId: __gigg.accountId,
      boardType: EmbedTypes.MASONRY,
      folderId: __gigg.folderId,
      scoreWeight: AnalyticsScores.SEE_MORE,
      firstEvent: {
        _id: window.giggFirstEvent?._id,
        createdDate: window.giggFirstEvent?.createdDate,
      },
      inViewEvent: {
        _id: window.latestViewEvent?._id,
        createdDate: window.latestViewEvent?.createdDate,
      },
    });
    setTimeout(() => {
      document.querySelector('masonry-board').shadowRoot.querySelector('masonry-layout').layout();
    }, 100);
  }

  openModal = (index) => {
    if (this.options.disableClicking) {
      return;
    }
    this.selectedPost = this.posts[index];
    this.selectedPostIndex = index;
    if (index === -1) {
      this.postsOpened += 1;
      return;
    }

    setHashParam('giggPost', this.selectedPost.id);

    this.postsOpened += 1;
    giggAnalytics.logEvent(AnalyticsEvents.OPEN_POST, {
      boardId: __gigg.accountId,
      boardType: EmbedTypes.MASONRY,
      folderId: __gigg.folderId,
      postIndex: index,
      postId: this.selectedPost.id,
      postType: this.selectedPost.service,
      mediaType: getMediaType(this.selectedPost.media),
      isCarousel: this.selectedPost.media?.length > 1,
      scoreWeight:
        this.postsOpened > 1 ? AnalyticsScores.SECOND_OPEN_POST : AnalyticsScores.OPEN_POST,
      firstEvent: {
        _id: window.giggFirstEvent?._id,
        createdDate: window.giggFirstEvent?.createdDate,
      },
    });
  };

  /**
   * Opens a post modal by post ID (used for hash-based routing)
   * @param {string} postId - The ID of the post to open
   */
  openPostByHashId = async (postId) => {
    const postIndex = this.posts.findIndex((post) => post.id === postId);

    if (postIndex !== -1) {
      this.selectedPost = this.posts[postIndex];
      this.selectedPostIndex = postIndex;
      this.requestUpdate();
    } else {
      try {
        const apiUrl = `${process.env.REACT_APP_CORE_API_URL}/public/boards/${config.accountId}/posts/${postId}`;
        const postResponse = await fetch(apiUrl);
        const postResult = await postResponse.json();
        const postDataWithId = {
          id: postId,
          ...postResult?.data,
        };
        this.selectedPost = postDataWithId;
        this.selectedPostIndex = -1;
        this.requestUpdate();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching post:', error);
        removeHashParam('giggPost');
      }
    }
  };

  handlePostFocused = (evt, item, index) => {
    if (evt.propertyName !== 'transform' || evt.elapsedTime < 1) {
      return;
    }
    if (!this.isHovered) {
      this.isHovered = true;
      this.postsFocused += 1;
      giggAnalytics.logEvent(AnalyticsEvents.FOCUS_POST, {
        boardId: __gigg.accountId,
        boardType: EmbedTypes.MASONRY,
        folderId: __gigg.folderId,
        postIndex: index,
        postId: item.id,
        postType: item.service,
        mediaType: getMediaType(item.media),
        isCarousel: item.media?.length > 1,
        hasCTA: !!item.customLink?.linkUrl,
        scoreWeight:
          this.postsFocused > 1 ? AnalyticsScores.SECOND_FOCUS_POST : AnalyticsScores.FOCUS_POST,
        firstEvent: {
          _id: window.giggFirstEvent?._id,
          createdDate: window.giggFirstEvent?.createdDate,
        },
        inViewEvent: {
          _id: window.latestViewEvent?._id,
          createdDate: window.latestViewEvent?.createdDate,
        },
      });
      return;
    }
    this.isHovered = false;
  };

  handleMediaError = (post, err) => {
    const { posts } = this;
    if (err.target.nodeName === 'VIDEO') {
      this.posts = posts.reduce((acc, next) => {
        const postObj =
          post.id === next.id
            ? { ...next, media: post.media.map((item) => ({ ...item, video: undefined })) }
            : next;
        acc.push(postObj);
        return acc;
      }, []);
    } else {
      this.posts = posts.filter((p) => post.id !== p.id);
    }
  };

  closeModal = () => {
    removeHashParam('giggPost');
    this.selectedPost = null;
    this.selectedPostIndex = null;
  };

  render() {
    const { showMoreBtnMasonry } = this.options;
    return html`
      <masonry-layout class="masonry" gap="16">
        ${this.posts.map((post, i) => {
          return html`<masonry-tile
            .post=${post}
            .index=${i}
            .options=${this.options}
            .onClick=${this.openModal}
            .handlePostFocused=${this.handlePostFocused}
            .handleMediaError=${this.handleMediaError}
          ></masonry-tile>`;
        })}
      </masonry-layout>
      ${showMoreBtnMasonry
        ? html`
            <div class="masonry__footer">
              <button
                class="masonry__more-btn"
                @click=${this.handleSeeMore}
                ?disabled=${this.loading}
              >
                ${this.loading ? t`Loading...` : t`See More`}
              </button>
            </div>
          `
        : ''}

      <post-modal
        .shareTitle=${this.options.shareTitle}
        .post=${this.selectedPost}
        .index=${this.selectedPostIndex}
        .disableLinks=${this.options.disableHashtagLinks}
        .disableDate=${this.options.disableDates}
        .onClose=${this.closeModal}
      />
    `;
  }
}

customElements.define('masonry-board', MasonryWebComponent);

const masonryElement = document.createElement('masonry-board');
document.getElementById(__gigg.domElementId).appendChild(masonryElement);
let isInView = false;

const observer = new IntersectionObserver(
  (entries) => {
    if (entries[0].isIntersecting === false && isInView === true) {
      giggAnalytics.logEvent(AnalyticsEvents.EMBED_UNVIEW, {
        boardId: __gigg.accountId,
        boardType: EmbedTypes.GRID,
        folderId: __gigg.folderId,
        firstEvent: {
          _id: window.giggFirstEvent?._id,
          createdDate: window.giggFirstEvent?.createdDate,
        },
        inViewEvent: {
          _id: window.latestViewEvent?._id,
          createdDate: window.latestViewEvent?.createdDate,
        },
      });
      isInView = false;
    }
    if (entries[0].isIntersecting === true) {
      isInView = true;
      giggAnalytics.logEvent(AnalyticsEvents.EMBED_VIEW, {
        boardId: __gigg.accountId,
        boardType: EmbedTypes.MASONRY,
        folderId: __gigg.folderId,
        firstEvent: {
          _id: window.giggFirstEvent?._id,
          createdDate: window.giggFirstEvent?.createdDate,
        },
      });
    }
  },
  { threshold: [0.5] }
);

observer.observe(masonryElement);
